import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Style from '../styles/ServerStatus.module.css';

const apiBase = process.env.REACT_APP_API_BASE || '';

function formatUptime(seconds = 0) {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return `${days}d ${hours}h ${minutes}m`;
}

function ServerStatus() {
  const [token, setToken] = useState(() => localStorage.getItem('dinoStatusToken') || '');
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const authHeaders = {
    Authorization: `Bearer ${token}`,
  };

  const loadStatus = async () => {
    if (!token) {
      setError('Enter the owner token to view server status.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const [statusResponse, logsResponse] = await Promise.all([
        fetch(`${apiBase}/api/admin/status`, { headers: authHeaders }),
        fetch(`${apiBase}/api/admin/logs?limit=50`, { headers: authHeaders }),
      ]);

      if (!statusResponse.ok || !logsResponse.ok) {
        throw new Error('Unable to load status. Check the token and API URL.');
      }

      const statusData = await statusResponse.json();
      const logsData = await logsResponse.json();

      setStatus(statusData);
      setLogs(logsData.logs || []);
      localStorage.setItem('dinoStatusToken', token);
    } catch (requestError) {
      setStatus(null);
      setLogs([]);
      setError(requestError.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className={Style.Page}>
      <Helmet>
        <title>Server Status - Dino Construction</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <section className={Style.Hero}>
        <div>
          <p>Owner tools</p>
          <h1>Server status</h1>
          <span>Check whether the estimate API is running and review recent app logs.</span>
        </div>
      </section>

      <section className={Style.Panel}>
        <div className={Style.TokenForm}>
          <label htmlFor="statusToken">Owner token</label>
          <div>
            <input
              id="statusToken"
              type="password"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              placeholder="Enter ADMIN_STATUS_TOKEN"
            />
            <button type="button" onClick={loadStatus} disabled={isLoading}>
              {isLoading ? 'Checking...' : 'Check Status'}
            </button>
          </div>
          {error && <p className={Style.Error}>{error}</p>}
        </div>

        {status && (
          <div className={Style.StatusGrid}>
            <div>
              <span>API</span>
              <strong>{status.status === 'ok' ? 'Running' : 'Check server'}</strong>
            </div>
            <div>
              <span>Uptime</span>
              <strong>{formatUptime(status.uptimeSeconds)}</strong>
            </div>
            <div>
              <span>Email</span>
              <strong>{status.emailConfigured ? 'Configured' : 'Missing setup'}</strong>
            </div>
            <div>
              <span>Rate limit</span>
              <strong>{status.rateLimit.maxRequests}/hour</strong>
            </div>
          </div>
        )}

        {status && (
          <div className={Style.Details}>
            <p><strong>Started:</strong> {new Date(status.startedAt).toLocaleString()}</p>
            <p><strong>Node:</strong> {status.nodeVersion}</p>
            <p><strong>From:</strong> {status.mailFrom}</p>
            <p><strong>To:</strong> {status.mailTo}</p>
          </div>
        )}

        {logs.length > 0 && (
          <div className={Style.Logs}>
            <div className={Style.LogHeader}>
              <h2>Recent logs</h2>
              <button type="button" onClick={loadStatus} disabled={isLoading}>Refresh</button>
            </div>
            <div className={Style.LogList}>
              {logs.map((log) => (
                <article className={Style.LogRow} key={log.id}>
                  <span className={Style[log.level] || Style.info}>{log.level}</span>
                  <div>
                    <strong>{log.message}</strong>
                    <p>{new Date(log.timestamp).toLocaleString()}</p>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <pre>{JSON.stringify(log.details, null, 2)}</pre>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </section>
    </main>
  );
}

export default ServerStatus;
